import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GuestManagement = () => {
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Mock guest data
  const guests = [
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah@email.com',
      phone: '(555) 123-4567',
      avatar: 'SJ',
      totalSessions: 8,
      totalSpent: 1200,
      lastVisit: '2025-01-20',
      wellnessGoals: ['Stress Relief', 'Energy Boost'],
      preferredServices: ['EES Sessions', 'Meditation'],
      notes: 'First-time EES user, very interested in wellness programs. Prefers morning sessions.',
      upcomingBookings: 2,
      wellnessScore: 85,
      satisfactionRating: 4.8,
      healthConcerns: ['Chronic fatigue', 'Work stress'],
      emergencyContact: 'John Johnson - (555) 123-4568'
    },
    {
      id: 2,
      name: 'Michael Rodriguez',
      email: 'michael@email.com',
      phone: '(555) 234-5678',
      avatar: 'MR',
      totalSessions: 15,
      totalSpent: 2800,
      lastVisit: '2025-01-22',
      wellnessGoals: ['Pain Relief', 'Better Sleep'],
      preferredServices: ['Weekly Packages', 'Sound Healing'],
      notes: 'Regular client, interested in detox programs. Has mobility concerns.',
      upcomingBookings: 1,
      wellnessScore: 92,
      satisfactionRating: 4.9,
      healthConcerns: ['Lower back pain', 'Insomnia'],
      emergencyContact: 'Maria Rodriguez - (555) 234-5679'
    },
    {
      id: 3,
      name: 'Emma Thompson',
      email: 'emma@email.com',
      phone: '(555) 345-6789',
      avatar: 'ET',
      totalSessions: 3,
      totalSpent: 450,
      lastVisit: '2025-01-18',
      wellnessGoals: ['Mental Clarity', 'Relaxation'],
      preferredServices: ['Day Passes', 'Yoga'],
      notes: 'New client, very enthusiastic about holistic wellness approaches.',
      upcomingBookings: 0,
      wellnessScore: 78,
      satisfactionRating: 4.7,
      healthConcerns: ['Anxiety', 'Work-life balance'],
      emergencyContact: 'David Thompson - (555) 345-6790'
    }
  ];

  const filteredGuests = guests.filter(guest =>
    guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guest.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleGuestSelect = (guest) => {
    setSelectedGuest(guest);
  };

  const handleSendMessage = (guestId) => {
    console.log('Sending message to guest:', guestId);
  };

  const handleScheduleFollowUp = (guestId) => {
    console.log('Scheduling follow-up for guest:', guestId);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Guest List */}
      <div className="lg:col-span-1">
        <div className="bg-surface rounded-lg shadow-elevation-1 overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Guest Management</h2>
            
            {/* Search */}
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                placeholder="Search guests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              />
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {filteredGuests.map((guest) => (
              <button
                key={guest.id}
                onClick={() => handleGuestSelect(guest)}
                className={`w-full p-4 text-left hover:bg-secondary-50 transition-colors duration-200 border-b border-border ${
                  selectedGuest?.id === guest.id ? 'bg-primary-50 border-primary-200' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">{guest.avatar}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-text-primary truncate">{guest.name}</h3>
                    <p className="text-sm text-text-secondary truncate">{guest.email}</p>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-xs text-success font-medium">
                        {guest.totalSessions} sessions
                      </span>
                      {guest.upcomingBookings > 0 && (
                        <span className="text-xs bg-accent-100 text-accent-600 px-2 py-1 rounded-full">
                          {guest.upcomingBookings} upcoming
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Guest Details */}
      <div className="lg:col-span-2">
        {selectedGuest ? (
          <div className="bg-surface rounded-lg shadow-elevation-1 overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-lg">{selectedGuest.avatar}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-text-primary">{selectedGuest.name}</h2>
                    <p className="text-text-secondary">{selectedGuest.email}</p>
                    <p className="text-text-secondary">{selectedGuest.phone}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleSendMessage(selectedGuest.id)}
                    className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-all duration-200"
                  >
                    <Icon name="MessageCircle" size={16} />
                    <span>Message</span>
                  </button>
                  <button
                    onClick={() => handleScheduleFollowUp(selectedGuest.id)}
                    className="flex items-center space-x-2 bg-accent text-white px-4 py-2 rounded-md hover:bg-accent-600 transition-all duration-200"
                  >
                    <Icon name="Calendar" size={16} />
                    <span>Follow Up</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 border-b border-border">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{selectedGuest.totalSessions}</div>
                <div className="text-sm text-text-secondary">Total Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-success">${selectedGuest.totalSpent}</div>
                <div className="text-sm text-text-secondary">Total Spent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">{selectedGuest.wellnessScore}</div>
                <div className="text-sm text-text-secondary">Wellness Score</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1">
                  <div className="text-2xl font-bold text-warning-600">{selectedGuest.satisfactionRating}</div>
                  <Icon name="Star" size={16} className="text-warning" />
                </div>
                <div className="text-sm text-text-secondary">Satisfaction</div>
              </div>
            </div>

            {/* Detailed Information */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Wellness Goals */}
                <div>
                  <h3 className="text-md font-medium text-text-primary mb-3">Wellness Goals</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedGuest.wellnessGoals.map((goal, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 bg-primary-100 text-primary text-sm rounded-full"
                      >
                        {goal}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Preferred Services */}
                <div>
                  <h3 className="text-md font-medium text-text-primary mb-3">Preferred Services</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedGuest.preferredServices.map((service, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 bg-accent-100 text-accent-600 text-sm rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Health Concerns */}
              <div>
                <h3 className="text-md font-medium text-text-primary mb-3">Health Concerns</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedGuest.healthConcerns.map((concern, index) => (
                    <span
                      key={index}
                      className="inline-block px-3 py-1 bg-warning-100 text-warning-600 text-sm rounded-full"
                    >
                      {concern}
                    </span>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-md font-medium text-text-primary mb-3">Notes</h3>
                <div className="bg-secondary-50 p-4 rounded-lg">
                  <p className="text-text-secondary text-sm">{selectedGuest.notes}</p>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-md font-medium text-text-primary mb-3">Emergency Contact</h3>
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="Phone" size={16} className="text-text-secondary" />
                  <span className="text-text-secondary">{selectedGuest.emergencyContact}</span>
                </div>
              </div>

              {/* Last Visit */}
              <div>
                <h3 className="text-md font-medium text-text-primary mb-3">Last Visit</h3>
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="Calendar" size={16} className="text-text-secondary" />
                  <span className="text-text-secondary">
                    {new Date(selectedGuest.lastVisit).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-surface rounded-lg shadow-elevation-1 p-12 text-center">
            <Icon name="Users" size={64} className="text-secondary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">Select a Guest</h3>
            <p className="text-text-secondary">Choose a guest from the list to view their details and wellness journey.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestManagement;